import { useState } from "react";
import {
  Button,
  Modal,
  Table,
  Text,
  Group,
  Badge,
  Stack,
  Tabs,
  ActionIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  ChartBarIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/solid";
import { apiMetrics } from "../../hooks/api";

type SortField =
  "avgDuration" | "maxDuration" | "count" | "duration" | "timestamp";
type SortDirection = "asc" | "desc";

interface ThSortableProps {
  children: React.ReactNode;
  sorted: boolean;
  reversed: boolean;
  onSort: () => void;
}

const ThSortable = ({
  children,
  sorted,
  reversed,
  onSort,
}: ThSortableProps) => {
  const Icon = sorted
    ? reversed
      ? ChevronUpIcon
      : ChevronDownIcon
    : ChevronUpDownIcon;
  return (
    <Table.Th>
      <UnstyledButton
        onClick={onSort}
        style={{ display: "flex", alignItems: "center", gap: 4 }}
      >
        <Text fw={600} size="sm">
          {children}
        </Text>
        <Icon width={14} height={14} style={{ opacity: sorted ? 1 : 0.5 }} />
      </UnstyledButton>
    </Table.Th>
  );
};

const isDevelopment = import.meta.env.DEV;

export const ApiMetricsPanel = () => {
  const [opened, setOpened] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [slowestSort, setSlowestSort] = useState<{
    field: SortField;
    direction: SortDirection;
  }>({
    field: "duration",
    direction: "desc",
  });
  const [endpointSort, setEndpointSort] = useState<{
    field: SortField;
    direction: SortDirection;
  }>({
    field: "maxDuration",
    direction: "desc",
  });
  const [allSort, setAllSort] = useState<{
    field: SortField;
    direction: SortDirection;
  }>({
    field: "timestamp",
    direction: "desc",
  });

  const summary = apiMetrics.getSummary();

  const refresh = () => setRefreshKey((k) => k + 1);

  if (!isDevelopment) {
    return null;
  }

  return (
    <>
      {/* Floating button */}
      <Tooltip label="API Metrics">
        <ActionIcon
          onClick={() => setOpened(true)}
          aria-label="API Metrics"
          size="lg"
          variant="filled"
          color="violet"
          radius="xl"
          style={{
            position: "fixed",
            bottom: 20,
            left: 20,
            zIndex: 1000,
          }}
        >
          <ChartBarIcon width={20} height={20} />
        </ActionIcon>
      </Tooltip>

      {/* Modal with metrics */}
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <Group>
            <ChartBarIcon width={20} height={20} />
            <Text fw={600}>API Performance Metrics</Text>
          </Group>
        }
        size="90%"
        key={refreshKey}
      >
        <Stack gap="md">
          <Group justify="space-between">
            <Group>
              <Badge color="blue" size="lg">
                Total: {summary?.totalRequests || 0} requests
              </Badge>
              <Badge color="green" size="lg">
                Avg: {summary?.avgDuration || 0}ms
              </Badge>
            </Group>
            <Button
              leftSection={<ArrowPathIcon width={16} height={16} />}
              variant="light"
              size="xs"
              onClick={refresh}
            >
              Refresh
            </Button>
          </Group>

          <Tabs defaultValue="slowest">
            <Tabs.List>
              <Tabs.Tab value="slowest">🐢 Slowest</Tabs.Tab>
              <Tabs.Tab value="endpoints">📈 By Endpoint</Tabs.Tab>
              <Tabs.Tab value="all">📋 All Requests</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="slowest" pt="md">
              {summary?.slowest && summary.slowest.length > 0 ? (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Method</Table.Th>
                      <Table.Th>URL</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <ThSortable
                        sorted={slowestSort.field === "duration"}
                        reversed={slowestSort.direction === "asc"}
                        onSort={() =>
                          setSlowestSort((prev) => ({
                            field: "duration",
                            direction:
                              prev.field === "duration" &&
                              prev.direction === "desc"
                                ? "asc"
                                : "desc",
                          }))
                        }
                      >
                        Duration
                      </ThSortable>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {[...summary.slowest]
                      .sort((a, b) => {
                        const multiplier =
                          slowestSort.direction === "desc" ? -1 : 1;
                        return (a.duration - b.duration) * multiplier;
                      })
                      .map((m, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Badge
                              color={m.method === "GET" ? "blue" : "orange"}
                              variant="light"
                            >
                              {m.method}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{m.url}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={
                                m.status === 200 || m.status === 201
                                  ? "green"
                                  : "red"
                              }
                            >
                              {m.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              fw={600}
                              c={m.duration > 1000 ? "red" : "dimmed"}
                            >
                              {m.duration.toFixed(2)}ms
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No requests recorded yet. Make some API calls!
                </Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="endpoints" pt="md">
              {summary?.byEndpoint &&
              Object.keys(summary.byEndpoint).length > 0 ? (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Method</Table.Th>
                      <Table.Th>URL</Table.Th>
                      <Table.Th>Requests</Table.Th>
                      <ThSortable
                        sorted={endpointSort.field === "avgDuration"}
                        reversed={endpointSort.direction === "asc"}
                        onSort={() =>
                          setEndpointSort((prev) => ({
                            field: "avgDuration",
                            direction:
                              prev.field === "avgDuration" &&
                              prev.direction === "desc"
                                ? "asc"
                                : "desc",
                          }))
                        }
                      >
                        Avg Duration
                      </ThSortable>
                      <ThSortable
                        sorted={endpointSort.field === "maxDuration"}
                        reversed={endpointSort.direction === "asc"}
                        onSort={() =>
                          setEndpointSort((prev) => ({
                            field: "maxDuration",
                            direction:
                              prev.field === "maxDuration" &&
                              prev.direction === "desc"
                                ? "asc"
                                : "desc",
                          }))
                        }
                      >
                        Max Duration
                      </ThSortable>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {Object.entries(summary.byEndpoint)
                      .sort((a, b) => {
                        const aAvg = a[1].totalDuration / a[1].count;
                        const bAvg = b[1].totalDuration / b[1].count;
                        const multiplier =
                          endpointSort.direction === "desc" ? -1 : 1;
                        if (endpointSort.field === "avgDuration") {
                          return (aAvg - bAvg) * multiplier;
                        }
                        return (
                          (a[1].maxDuration - b[1].maxDuration) * multiplier
                        );
                      })
                      .map(([endpoint, stats], i) => {
                        const [method, ...urlParts] = endpoint.split(" ");
                        const url = urlParts.join(" ");
                        return (
                          <Table.Tr key={i}>
                            <Table.Td>
                              <Badge
                                color={method === "GET" ? "blue" : "orange"}
                                variant="light"
                              >
                                {method}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{url}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light">{stats.count}</Badge>
                            </Table.Td>
                            <Table.Td>
                              {(stats.totalDuration / stats.count).toFixed(2)}ms
                            </Table.Td>
                            <Table.Td>
                              <Text
                                fw={600}
                                c={stats.maxDuration > 1000 ? "red" : "dimmed"}
                              >
                                {stats.maxDuration.toFixed(2)}ms
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No requests recorded yet. Make some API calls!
                </Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="all" pt="md">
              {summary ? (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <ThSortable
                        sorted={allSort.field === "timestamp"}
                        reversed={allSort.direction === "asc"}
                        onSort={() =>
                          setAllSort((prev) => ({
                            field: "timestamp",
                            direction:
                              prev.field === "timestamp" &&
                              prev.direction === "desc"
                                ? "asc"
                                : "desc",
                          }))
                        }
                      >
                        Time
                      </ThSortable>
                      <Table.Th>Method</Table.Th>
                      <Table.Th>URL</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <ThSortable
                        sorted={allSort.field === "duration"}
                        reversed={allSort.direction === "asc"}
                        onSort={() =>
                          setAllSort((prev) => ({
                            field: "duration",
                            direction:
                              prev.field === "duration" &&
                              prev.direction === "desc"
                                ? "asc"
                                : "desc",
                          }))
                        }
                      >
                        Duration
                      </ThSortable>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {apiMetrics
                      .getMetrics()
                      .sort((a, b) => {
                        const multiplier =
                          allSort.direction === "desc" ? -1 : 1;
                        if (allSort.field === "duration") {
                          return (a.duration - b.duration) * multiplier;
                        }
                        // timestamp sort
                        return (
                          (a.timestamp.getTime() - b.timestamp.getTime()) *
                          multiplier
                        );
                      })
                      .map((m, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Text size="xs" c="dimmed">
                              {m.timestamp.toLocaleTimeString()}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={m.method === "GET" ? "blue" : "orange"}
                              variant="light"
                              size="sm"
                            >
                              {m.method}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{m.url}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              color={
                                m.status === 200 || m.status === 201
                                  ? "green"
                                  : "red"
                              }
                            >
                              {m.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              fw={600}
                              c={m.duration > 1000 ? "red" : "dimmed"}
                            >
                              {m.duration.toFixed(2)}ms
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No requests recorded yet. Make some API calls!
                </Text>
              )}
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Modal>
    </>
  );
};
